import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * Spec 032 — Application Profile DTOs.
 *
 * The Zod schemas are exported alongside the class-validator DTOs so the
 * client (libs/schema/src/application-profile) can reuse the exact same
 * shape — single source of truth for "what's an Application Profile."
 */

const remotePreferenceSchema = z.enum(["ON_SITE", "HYBRID", "REMOTE_ONLY"]);

const nullableUrl = z.string().url().optional().nullable();
const nullableShortText = z.string().trim().max(200).optional().nullable();

/**
 * Editable fields on the Application Profile. All optional → PATCH semantics
 * on PUT /me/application-profile; only supplied fields are updated.
 */
export const updateApplicationProfileSchema = z.object({
  phone: z.string().trim().max(30).optional().nullable(),
  currentLocation: nullableShortText,
  currentCompany: nullableShortText,

  linkedinUrl: nullableUrl,
  githubUrl: nullableUrl,
  portfolioUrl: nullableUrl,
  twitterUrl: nullableUrl,
  otherUrl: nullableUrl,

  usWorkAuthorized: z.boolean().optional().nullable(),
  needsSponsorshipNow: z.boolean().optional().nullable(),
  needsSponsorshipFuture: z.boolean().optional().nullable(),

  // Spec 032 follow-up — country-agnostic work eligibility.
  // ISO 3166-1 alpha-2 codes, e.g. ["US", "GB", "CA"]. The field resolver
  // matches any "authorised to work in [country]" question against these
  // lists. Validates each entry as 2-letter uppercase to keep storage tight.
  workAuthorizedCountries: z
    .array(
      z
        .string()
        .trim()
        .length(2)
        .regex(/^[A-Z]{2}$/),
    )
    .max(50)
    .optional(),
  needsSponsorshipCountries: z
    .array(
      z
        .string()
        .trim()
        .length(2)
        .regex(/^[A-Z]{2}$/),
    )
    .max(50)
    .optional(),

  desiredCompensationCents: z.number().int().min(0).max(1_000_000_000).optional().nullable(),
  noticePeriodDays: z.number().int().min(0).max(365).optional().nullable(),
  willingToRelocate: z.boolean().optional().nullable(),
  remotePreference: remotePreferenceSchema.optional().nullable(),

  howDidYouHear: z.string().trim().max(500).optional().nullable(),

  isAgeEligible: z.boolean().optional().nullable(),
  hasPriorEmploymentHere: z.boolean().optional().nullable(),
  hasNonCompete: z.boolean().optional().nullable(),

  agreementConsentGranted: z.boolean().optional(),
});

export class UpdateApplicationProfileDto extends createZodDto(updateApplicationProfileSchema) {}

/**
 * Response shape returned by GET / PUT /me/application-profile. Extends the
 * editable shape with server-managed read-only fields (id, timestamps, derived
 * summary counts). `eeoConfigured` is a boolean only — the encrypted EEO
 * payload is never returned from this endpoint (see contracts/api.md).
 */
export const applicationProfileResponseSchema = updateApplicationProfileSchema.extend({
  id: z.string(),
  agreementConsentGrantedAt: z.string().datetime().nullable(),
  // Spec 032 FR-020 — id of the résumé that seeded the one-time pre-fill,
  // or null if it never happened. The client uses this to decide whether to
  // show the stale-profile notice on subsequent résumé imports (FR-021).
  prefilledFromResumeId: z.string().nullable(),
  eeoConfigured: z.boolean(),
  customAnswerCount: z.number().int().min(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ApplicationProfile = z.infer<typeof applicationProfileResponseSchema>;
